import { useState, useCallback, useEffect } from 'react'
import { FileIcon, TrashIcon, ImageIcon } from '@/components/icons'
import { cn } from '@/lib/utils'
import { formatFileSize } from '@/utils/tools'
import { useDropzone } from 'react-dropzone'
import { Button as ButtonComponent } from '@/components/ui/button'
import { useMutation, useQuery } from '@apollo/client'
import { AdCreativeAsset, ButtonProps } from '@/types'
import FileUploadLineProgressBar from '@/components/ui/FileUploadLineProgressBar'
import { constructProxyUrl } from '@/utils/proxy'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import {
    START_CUSTOM_THUMBNAIL_UPLOAD,
    GET_CUSTOM_THUMBNAIL_BY_ASSET,
    DELETE_CUSTOM_THUMBNAIL_UPLOAD,
    CANCEL_CUSTOM_THUMBNAIL_UPLOAD,
} from '@/graphql/custom-thumbnail-upload-queries'

// Type cast for the Button component from JSX file
const Button = ButtonComponent as React.FC<ButtonProps>

const ALLOWED_IMAGE_FORMATS = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/gif': ['.gif'],
    'image/webp': ['.webp'],
}

interface UploaderProps {
    selectedFile: File | null
    handleRemoveFile: (e: React.MouseEvent) => void
    uploadState: 'idle' | 'uploading' | 'uploaded' | 'failed'
    uploadProgress: number
    uploadedSize?: number
    handleUpload: () => void
    handleCancelUpload?: () => void
    errorMessage?: string
    thumbnailUrl?: string
}

interface DropzoneProps {
    getRootProps: () => any
    isDragActive: boolean
    getInputProps: () => any
}

const Uploader = ({
    selectedFile,
    handleRemoveFile,
    uploadState,
    uploadProgress,
    uploadedSize,
    handleUpload,
    errorMessage,
    thumbnailUrl,
    handleCancelUpload,
}: UploaderProps) => {
    if (!selectedFile) return null

    // Get file extension from the file name
    const getFileExtension = (fileName: string) => {
        const lastDot = fileName.lastIndexOf('.')
        if (lastDot === -1) return ''
        return fileName.substring(lastDot + 1).toUpperCase()
    }

    const fileExtension = getFileExtension(selectedFile.name)

    return (
        <div className="tw:flex tw:flex-col tw:gap-2">
            <span className="tw:text-white tw:text-sm tw:font-semibold tw:pl-6">
                {uploadState === 'uploaded' ? 'Upload Complete' : 'Ready for Uploading'}
            </span>
            <div className="tw:px-7 tw:py-6 tw:rounded-xs tw:border-t tw:border-b">
                <div className="tw:flex tw:items-center tw:justify-between">
                    <div className="tw:flex tw:items-center tw:gap-2 tw:w-full">
                        <div className="tw:relative tw:flex tw:justify-center">
                            <div className="tw:bg-gray-300 tw:rounded-xs tw:p-2">
                                <ImageIcon />
                            </div>
                            {fileExtension && (
                                <div className="tw:absolute tw:bg-blue-300 tw:px-1 tw:font-semibold tw:text-[10px] tw:rounded-xs tw:-bottom-[7px]">
                                    {fileExtension}
                                </div>
                            )}
                        </div>
                        <div className="tw:flex tw:flex-col tw:w-full">
                            <span className="tw:text-white tw:text-sm tw:font-medium tw:mb-0.5">
                                {selectedFile.name}
                            </span>
                            {uploadState !== 'uploading' && (
                                <span className="tw:text-gray-200 tw:text-xs">
                                    Size: {formatFileSize(selectedFile.size)}
                                </span>
                            )}

                            {uploadState === 'uploading' && (
                                <div className="tw:flex tw:flex-col tw:gap-1.5">
                                    <div className="tw:text-xs tw:text-gray-200">
                                        {uploadedSize && selectedFile.size > 0
                                            ? `${formatFileSize(uploadedSize)} / ${formatFileSize(selectedFile.size)} · ${uploadProgress}%`
                                            : `${uploadProgress}% uploaded`}
                                    </div>
                                    <FileUploadLineProgressBar percentage={uploadProgress} />
                                </div>
                            )}
                        </div>
                    </div>
                    {uploadState !== 'uploading' && (
                        <button
                            onClick={handleRemoveFile}
                            className="tw:text-gray-200 tw:hover:text-white tw:transition-colors"
                        >
                            <TrashIcon size={24} className="tw:text-blue-500" />
                        </button>
                    )}
                </div>

                {uploadState === 'failed' && errorMessage && (
                    <div className="tw:mt-4 tw:text-red-400 tw:text-sm">Error: {errorMessage}</div>
                )}

                {uploadState === 'uploaded' && thumbnailUrl && (
                    <div className="tw:mt-4">
                        <div className="tw:text-green-400 tw:text-sm tw:mb-2">✓ Thumbnail uploaded successfully</div>
                        <img
                            src={thumbnailUrl}
                            alt="Uploaded thumbnail"
                            className="tw:w-20 tw:h-20 tw:object-cover tw:rounded-xs"
                        />
                    </div>
                )}
            </div>
            <div className="tw:px-6 tw:flex tw:mt-1">
                {uploadState === 'uploading' ? (
                    <Button variant="destructiveSoft" onClick={handleCancelUpload}>
                        Cancel
                    </Button>
                ) : (
                    <Button onClick={handleUpload} disabled={uploadState === 'uploaded'}>
                        {uploadState === 'uploaded' ? 'Uploaded' : 'Upload'}
                    </Button>
                )}
            </div>
        </div>
    )
}

const Dropzone = ({ getRootProps, isDragActive, getInputProps }: DropzoneProps) => {
    return (
        <div
            {...getRootProps()}
            className={cn('tw:px-7 tw:py-6 tw:rounded-xs tw:cursor-pointer', {
                'tw:bg-gray-500/50': isDragActive,
                'tw:bg-gray-500': !isDragActive,
            })}
        >
            <input {...getInputProps()} />
            <div
                className={cn('tw:rounded-xs tw:flex tw:flex-row tw:gap-2', {
                    'tw:!bg-gray-500/50': isDragActive,
                    'tw:!bg-gray-500': !isDragActive,
                })}
            >
                <div className="tw:rounded-full tw:bg-gray-300 tw:size-10 tw:flex tw:items-center tw:justify-center">
                    <FileIcon />
                </div>
                <div className="tw:flex tw:flex-col">
                    <span className="tw:font-semibold tw:text-white">
                        <a href="#" className="tw:pr-1 tw:text-blue-400">
                            Choose a file
                        </a>
                        or drag & drop it here
                    </span>
                    <span className="tw:text-gray-200">Supported formats: JPEG, PNG, GIF, WebP</span>
                </div>
            </div>
        </div>
    )
}

const ThumbnailUploader = ({
    adCreativeAsset,
    onUploadStateChange,
}: {
    adCreativeAsset: AdCreativeAsset
    onUploadStateChange?: (state: 'idle' | 'uploading' | 'uploaded' | 'failed') => void
}) => {
    if (!adCreativeAsset) return null

    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'uploaded' | 'failed'>('idle')
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploadedSize, setUploadedSize] = useState<number>(0)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [thumbnailUrl, setThumbnailUrl] = useState<string>('')
    const [thumbnailId, setThumbnailId] = useState<string>('')

    // Delete confirmation state
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        isProcessing: false,
    })

    const [startUpload] = useMutation(START_CUSTOM_THUMBNAIL_UPLOAD)
    const [deleteCustomThumbnail] = useMutation(DELETE_CUSTOM_THUMBNAIL_UPLOAD)
    const [cancelUpload] = useMutation(CANCEL_CUSTOM_THUMBNAIL_UPLOAD)

    // Poll for upload progress and check for existing custom thumbnail
    const { data: existingThumbnailData } = useQuery(GET_CUSTOM_THUMBNAIL_BY_ASSET, {
        variables: { adCreativeAssetId: adCreativeAsset.id },
        skip: !adCreativeAsset.id,
        pollInterval: uploadState === 'uploading' ? 1000 : 0, // Poll every second while uploading
    })

    // Update UI based on upload progress and existing thumbnail
    useEffect(() => {
        if (existingThumbnailData?.CustomThumbnailUpload?.customThumbnailUploadByAssetId) {
            const upload = existingThumbnailData.CustomThumbnailUpload.customThumbnailUploadByAssetId
            setUploadProgress(upload.upload_progress || 0)
            setUploadedSize(upload.uploaded_size || 0)
            setThumbnailId(upload.id || '')

            if (upload.upload_status === 'completed') {
                setUploadState('uploaded')
                setThumbnailUrl(upload.thumbnail_url || '')
            } else if (upload.upload_status === 'failed' || upload.upload_status === 'cancelled') {
                setUploadState('failed')
                setErrorMessage(upload.error_message || 'Upload failed')
            } else if (upload.upload_status === 'uploading') {
                setUploadState('uploading')
            }
        }
    }, [existingThumbnailData])

    // Notify parent when upload state changes
    useEffect(() => {
        onUploadStateChange?.(uploadState)
    }, [uploadState, onUploadStateChange])

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0])
            setUploadState('idle')
            setUploadProgress(0)
            setUploadedSize(0)
            setErrorMessage('')
            setThumbnailUrl('')
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ALLOWED_IMAGE_FORMATS,
        multiple: false,
    })

    const handleUpload = async () => {
        if (!selectedFile || !adCreativeAsset.id) return

        setUploadState('uploading')
        setUploadProgress(0)
        setUploadedSize(0)
        setErrorMessage('')

        const result = await startUpload({
            variables: {
                input: {
                    file: selectedFile,
                    ad_creative_asset_id: adCreativeAsset.id,
                },
            },
        })

        if (result.data?.startCustomThumbnailUpload?.upload?.id) {
            const uploadId = result.data.startCustomThumbnailUpload.upload.id
            setThumbnailId(uploadId)
        }
    }

    const handleRemoveFile = (e: React.MouseEvent) => {
        e.stopPropagation()
        setSelectedFile(null)
        setUploadState('idle')
        setUploadProgress(0)
        setUploadedSize(0)
        setErrorMessage('')
        setThumbnailUrl('')
    }

    // Handle cancel upload
    const handleCancelUpload = async () => {
        if (!thumbnailId) return

        try {
            await cancelUpload({ variables: { id: thumbnailId } })

            // Reset state after cancellation
            setUploadState('idle')
            setSelectedFile(null)
            setUploadProgress(0)
            setUploadedSize(0)
            setErrorMessage('Upload cancelled')
            setThumbnailUrl('')
            setThumbnailId('')
        } catch (error) {
            console.error('Error cancelling upload:', error)
            setErrorMessage('Failed to cancel upload')
        }
    }

    // Open delete confirmation dialog
    const handleDeleteClick = () => {
        setDeleteConfirmation({
            isOpen: true,
            isProcessing: false,
        })
    }

    // Handle delete confirmation
    const handleDeleteConfirm = async () => {
        if (!thumbnailId) return

        setDeleteConfirmation((prev) => ({ ...prev, isProcessing: true }))

        try {
            await deleteCustomThumbnail({
                variables: { id: thumbnailId },
            })

            // Reset state after successful deletion
            setUploadState('idle')
            setThumbnailUrl('')
            setThumbnailId('')
            setSelectedFile(null)
            setUploadProgress(0)
            setUploadedSize(0)
            setErrorMessage('')

            // Close dialog
            setDeleteConfirmation({
                isOpen: false,
                isProcessing: false,
            })
        } catch (error) {
            console.error('Error deleting thumbnail:', error)
            setDeleteConfirmation((prev) => ({ ...prev, isProcessing: false }))
            // Could add error handling here
        }
    }

    // Close delete confirmation dialog
    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            isProcessing: false,
        })
    }

    // Show existing uploaded thumbnail when upload is complete and we have a thumbnail URL
    const showExistingThumbnail = uploadState === 'uploaded' && thumbnailUrl

    return (
        <>
            {selectedFile && uploadState !== 'uploaded' && (
                <Uploader
                    selectedFile={selectedFile}
                    handleRemoveFile={handleRemoveFile}
                    uploadState={uploadState}
                    uploadProgress={uploadProgress}
                    uploadedSize={uploadedSize}
                    handleUpload={handleUpload}
                    errorMessage={errorMessage}
                    thumbnailUrl={thumbnailUrl}
                    handleCancelUpload={handleCancelUpload}
                />
            )}

            {showExistingThumbnail && (
                <>
                    <div className="tw:px-6">
                        <div className="tw:text-sm tw:font-semibold tw:mb-2">Thumbnail</div>
                        <img
                            src={constructProxyUrl(thumbnailUrl)}
                            className="tw:max-h-72 tw:rounded-xs tw:object-contain"
                        />
                        <div className="tw:mt-4">
                            <Button variant="destructiveSoft" onClick={handleDeleteClick}>
                                Delete
                            </Button>
                        </div>
                    </div>
                </>
            )}

            {!selectedFile && !showExistingThumbnail && (
                <div className="tw:px-6">
                    <Dropzone getRootProps={getRootProps} isDragActive={isDragActive} getInputProps={getInputProps} />
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                isOpen={deleteConfirmation.isOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Custom Thumbnail"
                description="Are you sure you want to delete this custom thumbnail?"
                confirmText="Yes, delete"
                cancelText="Cancel"
                variant="destructive"
                buttonClassName=""
                isProcessing={deleteConfirmation.isProcessing}
            />
        </>
    )
}

export default ThumbnailUploader
